<template>
<div class="wrapper">
    <modal :no-footer="true" size="large" @close="$emit('close')">
        <template slot="header">
            Archiving {{ facility.facility_number }} ({{ facility.prime_site_id }})
        </template>

        <template slot="content">
            <div v-if="stage === 0">
                <p>Welcome! You are about to archive a facility. This process is irreversible. Please make sure you wish to perform the following actions before proceeding:</p>

                <ul>
                    <li>Backing up and deleting all {{ facility.stats.tenant_count.toLocaleString() }} tenants.</li>
                    <li>Backing up and resetting all stats data.</li>
                    <li>Resetting period specific facility fields (letter dates, upload counts, etc.).</li>
                </ul>

                <p>Note: you will be able to set the new letter dates after continuing.</p>

                <div class="buttons">
                    <span></span>

                    <button type="button" @click="stage = 1">Continue</button>
                </div>
            </div>

            <div v-else-if="stage === 1">
                <p>These are the proposed new letter dates. By default, they are the current letter dates plus 3 months.</p>

                <div v-if="loadingDates">
                    <p>Loading dates...</p>
                </div>

                <div v-else-if="letterDates !== null" class="flex jc-sb ai-fs">
                    <div>
                        <label>Audit Letter Date:</label>
                        <datepicker
                            :typeable="true"
                            :disabled="skipSettingLetterDates"
                            v-model="letterDates.initial_letter_date"
                            :format="'MM/dd/yyyy'"
                            :inline="true" />
                    </div>

                    <div>
                        <label>Reminder Date:</label>
                        <datepicker
                            :typeable="true"
                            :disabled="skipSettingLetterDates"
                            v-model="letterDates.reminder_letter_date"
                            :format="'MM/dd/yyyy'"
                            :inline="true" />
                    </div>

                    <div>
                        <label>Letter Effective Date:</label>
                        <datepicker
                            :typeable="true"
                            :disabled="skipSettingLetterDates"
                            v-model="letterDates.letter_effective_date"
                            :format="'MM/dd/yyyy'"
                            :inline="true" />
                    </div>
                </div>

                <div v-else>
                    <p>Uh oh. Letter dates could not be pulled for this facility.</p>
                </div>

                <div class="buttons">
                    <button class="text" type="button" @click="stage = 0">Back</button>

                    <div class="flex ai-c">
                        <span class="mr-3">
                            <checkbox
                                :checked="skipSettingLetterDates"
                                @click="skipSettingLetterDates = !skipSettingLetterDates">
                                <span>Skip setting letter dates</span>
                            </checkbox>
                        </span>

                        <button
                            class="success"
                            type="button"
                            :disabled="loadingArchive || loadingDates || letterDates === null"
                            @click="startArchiving">
                            Complete
                        </button>
                    </div>
                </div>
            </div>
        </template>
    </modal>
</div>
</template>

<script>
import http from "@/http"
import { objectToParams } from "@/utilities/form-data"

export default {
    props: {
        facility: {
            type: Object
        }
    },

    data () {
        return {
            letterDates: null,
            loadingArchive: false,
            loadingDates: false,
            skipSettingLetterDates: false,
            stage: 0
        }
    },

    watch: {
        stage (newValue) {
            if (newValue === 1) {
                if (this.letterDates === null) {
                    this.fetchLetterDates()
                }
            }
        }
    },

    methods: {
        async fetchLetterDates () {
            this.loadingDates = true

            try {
                const { data: response } = await http.get(`/api/facility-archives/${this.facility.id}/new-dates`)

                this.letterDates = {
                    letter_effective_date: response.letter_effective_date,
                    initial_letter_date: response.initial_letter_date,
                    reminder_letter_date: response.reminder_letter_date
                }
            } catch (ex) {
                this.$modalManager.add({ state: "bad", header: "Error", body: ex })
            } finally {
                this.loadingDates = false
            }
        },

        async startArchiving () {
            this.loadingArchive = true

            try {
                const params = objectToParams(this.letterDates)

                if (this.skipSettingLetterDates) {
                    params.append("skip_letter_dates", true)
                }

                const {
                    data: response
                } = await http.post(`/api/facility-archives/${this.facility.id}/execute`, params)

                const url = http.defaults.baseURL + `/api/facility-archives/download-export?filename=${response.export_filename}`
                window.open(url, "_blank")

                this.$emit("close")
            } catch (ex) {
                this.$modalManager.add({ state: "bad", header: "Error", body: ex })
            } finally {
                this.loadingArchive = false
            }
        }
    }
}
</script>

<style scoped>
.buttons {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-top: 20px;
}
</style>



// WEBPACK FOOTER //
// src/components/list-facilities-page/ArchiveFacilityModal.vue